import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useQueryClient } from '@tanstack/react-query'
import FormGroup from '@mui/material/FormGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import { useLayout } from 'hooks/useLayout'
import { Modal } from 'components/Modal/Modal'
import { useSnackbar } from 'stores/useSnackbar'
import { Box, Button, Typography } from '@mui/material'
import { ModalFullBody } from 'components/Modal/ModalFullBody'
import { ModalFullFooter } from 'components/Modal/ModalFullFooter'
import { ModalFullHeader } from 'components/Modal/ModalFullHeader'
import { type FlaggedPhoneNumber } from 'types/api'
import { ActivityButton } from 'components/ActivityButton'
import { usePutFlaggedPhoneNumber } from 'hooks/api/FlaggedPhoneNumbers/usePutFlaggedPhoneNumber'
import { formatPhoneNumber } from 'utils/phoneUtils'

export const EditFlaggedPhoneNumberModal = ({
  flaggedPhoneNumberToEdit,
  isModalOpen,
  toggleModalIsOpen,
}: {
  flaggedPhoneNumberToEdit: FlaggedPhoneNumber | undefined
  isModalOpen: boolean
  toggleModalIsOpen: () => void
}) => {
  const { isMobile } = useLayout()
  const { handleSubmit } = useForm()
  const [isEnabled, setIsEnabled] = useState(flaggedPhoneNumberToEdit?.enabled)
  const [isUpdateInProgress, setIsUpdateInProgress] = useState(false)
  const setSnackbarMessage = useSnackbar((state) => state.setMessage)
  const qc = useQueryClient()

  useEffect(() => {
    if (flaggedPhoneNumberToEdit) {
      setIsEnabled(flaggedPhoneNumberToEdit.enabled)
    }
  }, [flaggedPhoneNumberToEdit])

  const resetAll = () => {
    toggleModalIsOpen()
  }

  const { mutateAsync: putFlaggedPhoneNumberAsync } = usePutFlaggedPhoneNumber({
    onError: () => {
      setSnackbarMessage('Phone Number Update Failed')
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['/admin/flaggedPhoneNumbers'] })
      setSnackbarMessage('Phone Number Updated Successfully')
      toggleModalIsOpen()
    },
  })

  const updateRewardWrapper = handleSubmit(async () => {
    if (flaggedPhoneNumberToEdit) {
      setIsUpdateInProgress(true)
      await putFlaggedPhoneNumberAsync({
        ...flaggedPhoneNumberToEdit,
        enabled: isEnabled,
      })
      setIsUpdateInProgress(false)
    }
  })

  return (
    <Modal
      isOpen={isModalOpen}
      toggleIsOpen={() => {}}
      sx={{
        width: isMobile ? '100%' : '40%',
        height: isMobile ? '100%' : '40%',
      }}
    >
      <Box>
        <form onSubmit={updateRewardWrapper}>
          <ModalFullHeader
            toggleIsOpen={toggleModalIsOpen}
            title="Enable or Disable Phone Number"
          />
          <ModalFullBody heightModal={isMobile ? '100%' : '40%'}>
            <FormGroup sx={{ mb: 2 }}>
              <Typography sx={{ mb: 1 }}>
                <strong>Phone Number:</strong>{' '}
                {formatPhoneNumber(flaggedPhoneNumberToEdit?.phoneNumber)}
              </Typography>
              <Typography sx={{ mb: 1 }}>
                <strong>Type:</strong> {flaggedPhoneNumberToEdit?.type}
              </Typography>
              <Typography sx={{ mb: 2 }}>
                <strong>Carrier:</strong> {flaggedPhoneNumberToEdit?.carrier}
              </Typography>
            </FormGroup>

            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isEnabled}
                    onChange={(e) => setIsEnabled(e.target.checked)}
                  />
                }
                label="Enabled"
              />
            </FormGroup>
          </ModalFullBody>
          <ModalFullFooter>
            <Button onClick={resetAll}>Close</Button>
            <ActivityButton
              type="submit"
              variant="contained"
              active={isUpdateInProgress}
            >
              Save
            </ActivityButton>
          </ModalFullFooter>
        </form>
      </Box>
    </Modal>
  )
}
